import { RepoApiClient } from './repo-api.js';
import { FileTree } from './tree/FileTree.js';
import { FilePane, configureMonacoWorker } from './pane/FilePane.js';
import '../styles.css';

let _monacoWorkerReady = false;
async function ensureMonacoWorker() {
  if (_monacoWorkerReady) return;
  try {
    const workerModule = await import('monaco-editor/esm/vs/editor/editor.worker?worker');
    configureMonacoWorker(() => new workerModule.default());
    _monacoWorkerReady = true;
  } catch {
    _monacoWorkerReady = true;
  }
}

export class RepoEditor {
  constructor(container, options = {}) {
    if (typeof container === 'string') {
      this.container = document.querySelector(container);
    } else {
      this.container = container;
    }
    if (!this.container) {
      throw new Error('RepoEditor container not found');
    }

    this.options = {
      workspaceId: options.workspaceId || '',
      objectType: options.objectType || 'artefacts',
      objectId: options.objectId || '',
      taskId: options.taskId || null,
      branch: options.branch || 'main',
      readonly: Boolean(options.readonly),
      contentKind: options.contentKind || null,
      apiBase: options.apiBase || '',
      fetchFn: options.fetchFn || null,
      height: options.height || '520px',
      onSave: options.onSave || null,
      onHeadShaChange: options.onHeadShaChange || null,
      onError: options.onError || null,
      onSelectedPathChange: options.onSelectedPathChange || null,
    };

    this.api = new RepoApiClient({
      workspaceId: this.options.workspaceId,
      objectType: this.options.objectType,
      objectId: this.options.objectId,
      taskId: this.options.taskId,
      fetchFn: this.options.fetchFn,
      apiBase: this.options.apiBase,
    });

    this.metadata = null;
    this.selectedPath = null;
    this.headSha = null;
    this._buildLayout();
    this._wireComponents();
  }

  _buildLayout() {
    this.container.innerHTML = '';
    this.container.classList.add('repo-editor-root');
    this.container.style.height = this.options.height;

    this.statusEl = document.createElement('div');
    this.statusEl.className = 'repo-editor-status';

    this.layoutEl = document.createElement('div');
    this.layoutEl.className = 'repo-editor-layout';

    this.treeHost = document.createElement('div');
    this.treeHost.className = 'repo-editor-tree-host';

    this.paneHost = document.createElement('div');
    this.paneHost.className = 'repo-editor-pane-host';

    this.layoutEl.append(this.treeHost, this.paneHost);
    this.container.append(this.statusEl, this.layoutEl);
  }

  _wireComponents() {
    this.tree = new FileTree({
      container: this.treeHost,
      readonly: this.options.readonly,
      onSelect: (path) => this.openFile(path).catch((err) => this._handleError(err)),
      onError: (err) => this._handleError(err),
    });
    this.tree.setListTreeFn((path) => this.api.listTree(path));
    if (!this.options.readonly) {
      this.tree.setActionHandlers({
        onMkdir: (path) => this.api.mkdir(path),
        onNewFile: (path) => this.api.writeFile(path, '', { encoding: 'utf-8', message: `Create ${path}` }),
        onUpload: (parent, files) => this._uploadFiles(parent, files),
      });
    }

    this.pane = new FilePane({
      container: this.paneHost,
      readonly: this.options.readonly,
      onSave: (path, content, encoding) => this._saveFile(path, content, encoding),
      onDirty: (path, dirty) => this.tree.markDirty(path, dirty),
      onError: (err) => this._handleError(err),
    });
  }

  async init() {
    await ensureMonacoWorker();
    if (!this.options.workspaceId || !this.options.objectId) {
      this._setStatus('Save the object first to attach a repository.');
      return;
    }

    try {
      this.metadata = await this.api.metadata();
      if (!this.metadata.exists) {
        this._setStatus('Repository not initialized.');
        this._showInitButton();
        return;
      }
      this.headSha = this.metadata.head_sha;
      this._setStatus(this._statusText());
      await this.tree.refresh('');
    } catch (err) {
      this._handleError(err);
    }
  }

  _showInitButton() {
    if (this.options.readonly) return;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'repo-editor-init-btn';
    btn.textContent = 'Initialize repository';
    btn.addEventListener('click', () => {
      this.api.init(this.options.branch)
        .then((meta) => {
          this.metadata = meta;
          this.headSha = meta.head_sha;
          btn.remove();
          this._setStatus(this._statusText());
          return this.tree.refresh('');
        })
        .catch((err) => this._handleError(err));
    });
    this.statusEl.append(btn);
  }

  _statusText() {
    const scope = this.options.taskId ? `session ${this.options.taskId}` : 'canonical';
    const sha = this.headSha ? this.headSha.slice(0, 8) : '—';
    return `${this.options.objectType}/${this.options.objectId} · ${scope} · ${sha}`;
  }

  _setStatus(text) {
    this.statusEl.textContent = text;
  }

  async openFile(path) {
    const payload = await this.api.readFile(path);
    this.selectedPath = path;
    this.options.onSelectedPathChange?.(path);
    await this.pane.openFile(path, payload);
  }

  async _saveFile(path, content, encoding) {
    const result = await this.api.writeFile(path, content, {
      encoding,
      message: `Update ${path} via repo editor`,
    });
    if (result.head_sha) {
      this.headSha = result.head_sha;
      this.options.onHeadShaChange?.(result.head_sha);
      this._setStatus(this._statusText());
    }
    if (this.options.onSave) {
      await this.options.onSave({ path, headSha: result.head_sha });
    }
    return result;
  }

  async _uploadFiles(parentDir, files) {
    for (const file of files) {
      const relativePath = parentDir ? `${parentDir}/${file.name}` : file.name;
      const isText = (file.type || '').startsWith('text/')
        || /\.(txt|md|csv|json|xml|yaml|yml|html|css|js|ts|py|sql|sh)$/i.test(file.name);
      if (isText) {
        const content = await file.text();
        await this.api.writeFile(relativePath, content, {
          encoding: 'utf-8',
          message: `Upload ${relativePath}`,
        });
      } else {
        const buffer = await file.arrayBuffer();
        const bytes = new Uint8Array(buffer);
        let binary = '';
        bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
        const content = btoa(binary);
        await this.api.writeFile(relativePath, content, {
          encoding: 'base64',
          message: `Upload ${relativePath}`,
        });
      }
    }
    if (this.headSha !== null) {
      this.metadata = await this.api.metadata();
      this.headSha = this.metadata?.head_sha || this.headSha;
      this._setStatus(this._statusText());
    }
    await this.tree.refresh('');
  }

  updateOptions(next = {}) {
    Object.assign(this.options, next);
    this.api.workspaceId = this.options.workspaceId;
    this.api.objectType = this.options.objectType;
    this.api.objectId = this.options.objectId;
    this.api.taskId = this.options.taskId;
    if (next.height) this.container.style.height = next.height;
    if (typeof next.readonly === 'boolean') {
      this.pane.setReadonly(next.readonly);
    }
    if (next.workspaceId || next.objectId || next.objectType || next.taskId) {
      this.init().catch((err) => this._handleError(err));
    }
  }

  destroy() {
    this.pane?.disposeEditor();
    this.container.innerHTML = '';
  }

  _handleError(err) {
    const message = err?.message || String(err);
    this._setStatus(`Error: ${message}`);
    this.options.onError?.(err);
    console.error('[RepoEditor]', err);
  }
}

export { RepoApiClient } from './repo-api.js';
export default RepoEditor;
